import { useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDsgvoContext } from '@context'
import classNames from 'classnames'
import is from 'is_js'
import _ from '@strings'
import Text from '@components/Ui/Text'
import * as FieldTypes from '@components/Form/Types'
import styles from './Field.scss'

const Field = ({
  type,
  field_key,
  name,
  options,
  format,
  required,
  default_value,
  description,
}) => {
  const fieldIdentifier = field_key
  const { register, resetField, clearErrors, setValue, watch, errors } = useFormContext()
  const { hover, accepted } = useDsgvoContext()

  const isRequired = required === '1'

  const args = { required: isRequired, validate: {} }

  if (type === 'email') {
    args.validate.email = (v) => is.email(v)
  }

  if (type === 'number') {
    args.valueAsNumber = true
    args.validate.number = (v) => is.number(v)
  }

  if (
    ['text', 'email'].indexOf(type) > -1 ||
    ['appointment_phone', 'appointment_mobile'].indexOf(fieldIdentifier) > -1
  ) {
    args.validate.minLength = (v) => (v && v !== '' ? v.length > 2 : true)
  }

  // Custom Validations
  if (fieldIdentifier === 'appointment_zip') {
    args.validate.minLength = (v) => (v && v !== '' ? v?.trim().length >= 4 : true)
    args.validate.maxLength = (v) => (v && v !== '' ? v?.trim().length <= 5 : true)
  }

  if (fieldIdentifier === 'appointment_birthdate') {
    args.validate.checkLength = (v) => (v && v !== '' ? v?.trim().length === 10 : true)
  }
  // End Custom Validations
  register(fieldIdentifier, type === 'hidden' ? {} : args)

  const onChange = (value) => {
    setValue(fieldIdentifier, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const value = watch(fieldIdentifier)

  useEffect(() => {
    if (value === undefined) {
      setValue(fieldIdentifier, default_value)
    }
  }, [default_value])

  const getError = () => errors?.[fieldIdentifier]
  const hasError = getError

  const renderErrorText = () => {
    const err = getError()
    const msg = _(`errors.form.${err.type}`)

    if (msg) {
      return msg
    }

    return _('errors.form.default')
  }

  const renderField = useCallback(() => {
    switch (type) {
      case 'text':
      case 'number':
      case 'email':
      case 'hidden':
      case 'phone':
        if (format) {
          return (
            <FieldTypes.MaskedText
              setValue={onChange}
              value={value}
              mask={format}
              error={hasError()}
            />
          )
        }

        return <FieldTypes.Text type={type} setValue={onChange} value={value} error={hasError()} />
      case 'date':
        return <FieldTypes.Date type={type} setValue={onChange} value={value} error={hasError()} />
      case 'checkbox':
        if (fieldIdentifier === 'appointment_terms') {
          return (
            <FieldTypes.CheckBoxSpecial
              id={fieldIdentifier}
              options={options}
              setValue={onChange}
              value={value}
              description={description}
              error={hasError()}
            />
          )
        }

        return (
          <FieldTypes.CheckBox
            options={options}
            setValue={onChange}
            value={value}
            error={hasError()}
          />
        )
      case 'select':
        return (
          <FieldTypes.Select
            options={options}
            setValue={onChange}
            value={value}
            error={hasError()}
          />
        )
      case 'textarea':
        return <FieldTypes.TextArea setValue={onChange} value={value} error={hasError()} />
      default:
        return `Unknown Field Type: ${type}`
    }
  }, [type, value, errors?.[fieldIdentifier]])

  if (type === 'hidden') {
    return null
  }

  // const maybeHighlightDsgvo = () =>
  //   hover && fieldIdentifier === 'appointment_terms' && !accepted ? styles.highlight : null

  const maybeHighlightDsgvo = () => false

  return (
    <div className={classNames(styles.base, maybeHighlightDsgvo())}>
      {name && (
        <div className={styles.label}>
          {type !== 'toggle' && <Text type="robo_md_bold">{name}</Text>}
          {isRequired && <span className={styles.asterisk}>*</span>}
        </div>
      )}
      <div className={styles.field} data-e2e={`appointments-field--${field_key}`}>
        {value !== undefined && renderField()}
      </div>
      {hasError() && (
        <Text type="robo_md_bold" className={styles.error}>
          {renderErrorText()}
        </Text>
      )}
    </div>
  )
}

Field.defaultProps = {}
Field.propTypes = {}

export default Field
